/**
 * Wordle-style share grid.
 *
 * Turns the reader's *real* local call record into an emoji block that
 * survives a paste into WhatsApp/iMessage/X without spoiling any answer.
 * Nothing here is invented - no fake percentiles, no fake sample sizes.
 */

const TIERS: Array<{ min: number; label: string }> = [
  { min: 12, label: "Certified Reality Denier" },
  { min: 8, label: "Hard to Fool" },
  { min: 5, label: "Sharp Instinct" },
  { min: 3, label: "Warming Up" },
  { min: 1, label: "Rookie Caller" },
  { min: 0, label: "Fresh Meat" },
];

export function tierFor(bestStreak: number) {
  return (TIERS.find((t) => bestStreak >= t.min) ?? TIERS[TIERS.length - 1]).label;
}

export function buildShareGrid(opts: {
  recent: boolean[];
  streak: number;
  bestStreak: number;
  correct: number;
  total: number;
}) {
  const recent = opts.recent.slice(-10);
  const cells = recent.length ? recent.map((r) => (r ? "🟩" : "🟥")) : ["⬜"];
  const rows: string[] = [];
  for (let i = 0; i < cells.length; i += 5) rows.push(cells.slice(i, i + 5).join(""));

  const lines = [
    "Smartasaurus 🦖 real or nonsense?",
    rows.join("\n"),
    `${opts.correct}/${opts.total} called right · streak ${opts.streak}`,
    `Tier: ${tierFor(opts.bestStreak)}`,
    "",
    "Every story is true. Bet you can't tell which. 👇",
  ];
  return lines.join("\n");
}

/**
 * Peer-to-peer challenge link. Bakes the exact claim the sender just called
 * plus their live streak into the feed URL so the recipient lands on that same
 * card with a "beat this" banner instead of a random stream.
 */
export function challengeUrl(slug: string | null | undefined, streak: number) {
  const origin = typeof window !== "undefined" ? window.location.origin : "https://smartasaurus.com";
  const u = new URL(slug ? `/discovery/${slug}` : "/discoveries", origin);
  if (streak > 0) u.searchParams.set("streak", String(streak));
  u.searchParams.set("utm_source", "share_grid");
  u.searchParams.set("utm_medium", "social");
  u.searchParams.set("utm_campaign", "challenge");
  return u.toString();
}

/** Fires native share, falls back to clipboard. Returns "shared" | "copied" | "failed". */
export async function shareGrid(
  text: string,
  url: string,
): Promise<"shared" | "copied" | "failed"> {
  try {
    if (typeof navigator !== "undefined" && navigator.share) {
      await navigator.share({ title: "Smartasaurus", text, url });
      try {
        navigator.vibrate?.([20, 40]);
      } catch {
        /* unsupported */
      }
      return "shared";
    }
    await navigator.clipboard.writeText(`${text}\n${url}`);
    return "copied";
  } catch {
    return "failed";
  }
}
